import { z } from 'zod';

// Enums matching backend
const SizCategorySchema = z.enum([
  'clothing',
  'footwear',
  'head_protection',
  'eye_protection',
  'hand_protection',
  'respiratory_protection',
  'fall_protection',
  'hearing_protection',
  'other',
]);

const SizUnitSchema = z.enum(['piece', 'pair', 'kit', 'set']);

const SizNormItemStatusSchema = z.enum(['pending', 'partially_issued', 'issued', 'expiring_soon', 'overdue']);

// Reference schemas
export const PositionRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
  })
  .transform((data) => ({
    id: data.id,
    name: data.name,
  }));

export const OrgUnitRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
  })
  .transform((data) => ({
    id: data.id,
    name: data.name,
  }));

// Catalog item
export const SizCatalogItemSchema = z
  .object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    category: SizCategorySchema,
    unit: SizUnitSchema,
    gost_or_tr_ts: z.string().nullable().optional(),
    default_validity_months: z.number().int().nullable().optional(),
    is_active: z.boolean(),
  })
  .transform((data) => ({
    id: data.id,
    code: data.code,
    name: data.name,
    category: data.category,
    unit: data.unit,
    gostOrTrTs: data.gost_or_tr_ts ?? undefined,
    defaultValidityMonths: data.default_validity_months ?? undefined,
    isActive: data.is_active,
  }));

// Section keys (§5.1.1 GET /v1/ppe/sections)
export const ppeSectionKeyValues = ['my_ppe', 'employees_ppe', 'norms_catalog', 'siz_catalog', 'reports'] as const;

export type PpeSectionKey = (typeof ppeSectionKeyValues)[number];

export const ppeSectionKeyLabels: Record<PpeSectionKey, string> = {
  my_ppe: 'Мои СИЗ',
  employees_ppe: 'СИЗ сотрудников',
  norms_catalog: 'Справочник норм выдачи',
  siz_catalog: 'Реестр СИЗ',
  reports: 'Ведомости и потребность',
};

export const PpeSectionItemSchema = z
  .object({
    key: z.string(),
  })
  .transform((data) => ({
    key: data.key,
  }));

// API Responses
export const GetPpeSectionsResponseSchema = z
  .object({
    sections: z.array(PpeSectionItemSchema),
  })
  .transform((data) => ({
    sections: data.sections,
  }));

export const ListPpeCatalogResponseSchema = z
  .object({
    items: z.array(SizCatalogItemSchema),
    next_page_token: z.string().nullable().optional(),
  })
  .transform((data) => ({
    items: data.items,
    nextPageToken: data.next_page_token ?? undefined,
  }));

// Norm rule
export const SizNormRuleSchema = z
  .object({
    id: z.string().uuid(),
    siz_item: SizCatalogItemSchema,
    quantity: z.string(), // Decimal as string
    period_months: z.number().int().nullable().optional(),
    condition_code: z.string().nullable().optional(),
  })
  .transform((data) => ({
    id: data.id,
    sizItem: data.siz_item,
    quantity: data.quantity,
    periodMonths: data.period_months ?? undefined,
    conditionCode: data.condition_code ?? undefined,
  }));

// Norms by position
export const PpeNormsByPositionSchema = z
  .object({
    position: PositionRefSchema,
    rules: z.array(SizNormRuleSchema),
  })
  .transform((data) => ({
    position: data.position,
    rules: data.rules,
  }));

export const ListPpeNormsResponseSchema = z
  .object({
    positions: z.array(PpeNormsByPositionSchema),
    next_page_token: z.string().nullable().optional(),
  })
  .transform((data) => ({
    positions: data.positions,
    nextPageToken: data.next_page_token ?? undefined,
  }));

// Widget
export const PpeWidgetSummarySchema = z
  .object({
    total_employees: z.number().int(),
    fully_equipped: z.number().int(),
    partial_equipped: z.number().int(),
    pending_equipment: z.number().int(),
    expiring_soon: z.number().int(),
    overdue: z.number().int(),
  })
  .transform((data) => ({
    totalEmployees: data.total_employees,
    fullyEquipped: data.fully_equipped,
    partialEquipped: data.partial_equipped,
    pendingEquipment: data.pending_equipment,
    expiringSoon: data.expiring_soon,
    overdue: data.overdue,
  }));

export const GetPpeWidgetSummaryResponseSchema = z
  .object({
    widget: PpeWidgetSummarySchema,
  })
  .transform((data) => ({
    widget: data.widget,
  }));

// Employee list
export const PpeEmployeeListSchema = z
  .object({
    id: z.string().uuid(),
    full_name: z.string(),
    tab_number: z.string().nullable().optional(),
    is_active: z.boolean(),
    position: PositionRefSchema.nullable().optional(),
    org_unit: OrgUnitRefSchema,
    kit_status: SizNormItemStatusSchema.nullable().optional(),
    critical_items_count: z.number().int(),
  })
  .transform((data) => ({
    id: data.id,
    fullName: data.full_name,
    tabNumber: data.tab_number ?? undefined,
    isActive: data.is_active,
    position: data.position ?? undefined,
    orgUnit: data.org_unit,
    kitStatus: data.kit_status ?? undefined,
    criticalItemsCount: data.critical_items_count,
  }));

export const ListPpeEmployeesResponseSchema = z
  .object({
    employees: z.array(PpeEmployeeListSchema),
    next_page_token: z.string().nullable().optional(),
  })
  .transform((data) => ({
    employees: data.employees,
    nextPageToken: data.next_page_token ?? undefined,
  }));

// Employee profile
export const PpeEmployeeProfileSchema = z
  .object({
    id: z.string().uuid(),
    full_name: z.string(),
    tab_number: z.string().nullable().optional(),
    is_active: z.boolean(),
    position: PositionRefSchema.nullable().optional(),
    org_unit: OrgUnitRefSchema,
    sizes: z.record(z.string(), z.string()),
  })
  .transform((data) => ({
    id: data.id,
    fullName: data.full_name,
    tabNumber: data.tab_number ?? undefined,
    isActive: data.is_active,
    position: data.position ?? undefined,
    orgUnit: data.org_unit,
    sizes: data.sizes,
  }));

export const GetPpeEmployeeProfileResponseSchema = z.object({
  employee: PpeEmployeeProfileSchema,
});

// Required items
export const PpeRequiredItemSchema = z
  .object({
    siz_item_id: z.string().uuid(),
    name: z.string(),
    category: SizCategorySchema,
    unit: SizUnitSchema,
    required_quantity: z.string(), // Decimal as string
    issued_quantity: z.string(), // Decimal as string
    status: SizNormItemStatusSchema,
    next_replacement_at: z.string().nullable().optional(), // ISO date
  })
  .transform((data) => ({
    sizItemId: data.siz_item_id,
    name: data.name,
    category: data.category,
    unit: data.unit,
    requiredQuantity: data.required_quantity,
    issuedQuantity: data.issued_quantity,
    status: data.status,
    nextReplacementAt: data.next_replacement_at ?? undefined,
  }));

export const GetPpeEmployeeRequiredItemsResponseSchema = z
  .object({
    items: z.array(PpeRequiredItemSchema),
  })
  .transform((data) => ({
    items: data.items,
  }));

// Active issuances
export const PpeActiveIssuanceSchema = z
  .object({
    id: z.string().uuid(),
    siz_item: SizCatalogItemSchema,
    issued_at: z.string(), // ISO date
    quantity: z.string(), // Decimal as string
    size: z.string().nullable().optional(),
    inventory_number: z.string().nullable().optional(),
    valid_until: z.string(), // ISO date
    days_remaining: z.number().int(),
  })
  .transform((data) => ({
    id: data.id,
    sizItem: data.siz_item,
    issuedAt: data.issued_at,
    quantity: data.quantity,
    size: data.size ?? undefined,
    inventoryNumber: data.inventory_number ?? undefined,
    validUntil: data.valid_until,
    daysRemaining: data.days_remaining,
  }));

export const GetPpeEmployeeIssuancesResponseSchema = z
  .object({
    active: z.array(PpeActiveIssuanceSchema),
  })
  .transform((data) => ({
    active: data.active,
  }));

// History
export const PpeHistoryItemSchema = z
  .object({
    id: z.string().uuid(),
    type: z.enum(['issuance', 'return']),
    siz_item_name: z.string(),
    date: z.string(), // ISO date
    quantity: z.string(), // Decimal as string
    size: z.string().nullable().optional(),
    reason: z.string().nullable().optional(),
  })
  .transform((data) => ({
    id: data.id,
    type: data.type,
    sizItemName: data.siz_item_name,
    date: data.date,
    quantity: data.quantity,
    size: data.size ?? undefined,
    reason: data.reason ?? undefined,
  }));

export const GetPpeEmployeeHistoryResponseSchema = z
  .object({
    history: z.array(PpeHistoryItemSchema),
  })
  .transform((data) => ({
    history: data.history,
  }));

// Type exports
export type SizCategory = z.infer<typeof SizCategorySchema>;
export type SizUnit = z.infer<typeof SizUnitSchema>;
export type SizNormItemStatus = z.infer<typeof SizNormItemStatusSchema>;
export type PositionRef = z.infer<typeof PositionRefSchema>;
export type OrgUnitRef = z.infer<typeof OrgUnitRefSchema>;
export type SizCatalogItem = z.infer<typeof SizCatalogItemSchema>;
export type PpeSectionItem = z.infer<typeof PpeSectionItemSchema>;
export type SizNormRule = z.infer<typeof SizNormRuleSchema>;
export type PpeNormsByPosition = z.infer<typeof PpeNormsByPositionSchema>;
export type PpeWidgetSummary = z.infer<typeof PpeWidgetSummarySchema>;
export type PpeEmployeeList = z.infer<typeof PpeEmployeeListSchema>;
export type PpeEmployeeProfile = z.infer<typeof PpeEmployeeProfileSchema>;
export type PpeRequiredItem = z.infer<typeof PpeRequiredItemSchema>;
export type PpeActiveIssuance = z.infer<typeof PpeActiveIssuanceSchema>;
export type PpeHistoryItem = z.infer<typeof PpeHistoryItemSchema>;
